<script setup>
  import { ref, computed } from 'vue';

  const props = defineProps({
    tasks: {
      type: Array,
      default: () => []
    },
    activeTaskId: String,
    answeredCount: Number,
    isAgentic: {
      type: Boolean,
      default: false
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    disableAdd: {
      type: Boolean,
      default: false
    },
    exampleTask: {
      type: Object,
      required: true
    },
    showExampleMode: Boolean
  });
  
  const emit = defineEmits(['setActiveTaskId', 'addTask', 'toggleAnsweredFilter']);
  
  const showAnsweredOnly = ref(false);
  
  const toggleFilter = () => {
    showAnsweredOnly.value = !showAnsweredOnly.value;
    emit('toggleAnsweredFilter', showAnsweredOnly.value);
  };

  const displayedTasks = computed(() => {
    if (showAnsweredOnly.value) {
      return [props.exampleTask, ...props.tasks];
    }
    return props.tasks;
  });

  const displayedAnsweredCount = computed(() => {
    return (props.tasks || []).filter(task => (task.criteria?.length || 0) > 0).length;
  });

  const displayedTasksTotal = computed(() => {
    return Array.isArray(props.tasks) ? props.tasks.length : 0;
  });
  
  const isExampleTask = (idx) => {
    return showAnsweredOnly.value && idx === 0;
  };
  </script>
  
  <template>
    <div class="flex flex-col h-full border-r border-echoes-border-gray/40">
      <!-- Header -->
      <div class="p-3 border-b border-echoes-border-gray/40">
        <div class="text-xs uppercase text-echoes-primary font-semibold mb-2">Tasks</div>
        
        <div class="flex items-center justify-between">
          <div class="text-sm text-white/80">
            {{ displayedAnsweredCount }}/{{ displayedTasksTotal }} Rubric Ready
          </div>
          
          <div v-if="!props.isEditing" class="flex items-center gap-2">
            <span class="text-xs text-white/60">View Example</span>
            
            <button 
              role="switch"
              type="button"
              :aria-checked="showAnsweredOnly ? 'true' : 'false'"
              aria-label="Toggle example task"
              :class="[
                'relative inline-flex h-5 w-9 items-center rounded-full bg-echoes-border-light border-2 border-echoes-border-gray transition-colors duration-200 focus:outline-none',
              ]"
              data-testid="toggle-example-mode-button"
              @click="toggleFilter"
            >
              <span
                :class="[
                  'inline-block h-3.5 w-3.5 transform rounded-full transition-transform duration-200',
                  showAnsweredOnly ? 'translate-x-4 bg-green-500' : 'translate-x-0.5 bg-white'
                ]"
              />
            </button>
          </div>
        </div>
      </div>
  
      <!-- Task List -->
      <div class="flex-1 overflow-y-auto overflow-x-hidden p-3">
        <div class="space-y-3">
          <button 
            v-for="(task, idx) in displayedTasks" 
            :key="task.id" 
            @click="!props.isEditing && emit('setActiveTaskId', task.id)" 
            :disabled="props.isEditing"
            :class="[
              'w-full text-left py-2.5 px-2 rounded-md flex flex-col gap-2 transition-all duration-200 group',
              props.isEditing
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer',
              task.id === activeTaskId 
                ? 'bg-white/10 text-white border border-echoes-primary' 
                : 'bg-white/5 text-white/80 hover:bg-white/10 hover:text-white border border-transparent'
            ]"
            :data-testid="isExampleTask(idx) ? 'task-item-example' : `task-item-${idx + 1}`"
          >
            <!-- Task Header -->
            <div class="flex items-center">
              <span class="text-white/60 text-sm font-semibold min-w-[24px]">#{{ isExampleTask(idx) ? 0 : (showAnsweredOnly ? idx : idx + 1) }}</span>
              <span :class="['text-sm font-semibold truncate flex-1', isExampleTask(idx) ? 'uppercase' : '']">
                {{ isExampleTask(idx) ? 'EXAMPLE' : (task.prompt.trim() ? task.prompt.trim().slice(0, 45) + (task.prompt.trim().length > 45 ? '…' : '') : 'Untitled task') }}
              </span>
            </div>
  
            <!-- Task Meta Badges -->
            <div class="flex flex-wrap gap-1.5">
              <span
                v-if="(task.criteria?.length || 0) > 0"
                :class="[
                  'inline-flex items-center gap-1 rounded-full py-1 px-2.5 text-xs font-medium',
                  isExampleTask(idx)
                    ? 'bg-white/5 border border-white/40 text-white/80'
                    : 'bg-green-500/20 text-green-400 border border-green-500/30'
                ]"
              >
                <i class="uil uil-check-circle"></i>
                <span>Rubric Ready</span>
              </span>
              <span
                v-else
                :class="[
                  'inline-flex items-center gap-1 rounded-full py-1 px-2.5 text-xs font-medium',
                  isExampleTask(idx)
                    ? 'bg-white/5 border border-white/40 text-white/80'
                    : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                ]"
              >
                <i class="uil uil-exclamation-triangle"></i>
                <span>No Rubric</span>
              </span>


              <!-- Criteria Badge -->
              <span 
                v-if="task.criteria?.length > 0" 
                class="inline-flex items-center gap-1 bg-white/5 border border-white/40 text-white/80 rounded-full py-1 px-2.5 text-xs font-medium"
              >
                <i class="uil uil-list-ul"></i>
                <span>{{ task.criteria.length }} criteria</span>
              </span>
  
              <!-- References Badge -->
              <span 
                v-if="task.references?.length > 0" 
                :class="[
                  'inline-flex items-center gap-1 rounded-full py-1 px-2.5 text-xs font-medium',
                  isExampleTask(idx) 
                    ? 'bg-white/5 border border-white/40 text-white/80'
                    : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                ]"
                :title="`${task.references.length} reference file${task.references.length > 1 ? 's' : ''}`"
              >
                <i class="uil uil-paperclip"></i>
                <span>{{ task.references.length }}</span>
              </span>
  
              <!-- Tools Badge -->
              <span 
                v-if="task.tools?.length > 0" 
                :class="[
                  'inline-flex items-center gap-1 rounded-full font-medium',
                  isExampleTask(idx) 
                    ? 'bg-white/5 border border-white/40 text-white/80 py-1 px-2.5 text-xs'
                    : 'bg-echoes-background-gray text-white/60 border border-echoes-border-gray py-0.5 px-2 text-[10px]'
                ]"
              >
                <i class="uil uil-wrench"></i>
                <span>{{ task.tools.length }}</span>
              </span>
            </div>
          </button>
          
          <!-- Add Task Button -->
          <button 
            :disabled="props.isEditing || props.disableAdd"
            :class="[
              'w-full text-sm border-2 border-dashed border-white/40 text-white font-semibold rounded-md p-3 transition-all duration-200 flex items-center gap-2',
              props.isEditing || props.disableAdd
                ? 'border-white/50 cursor-not-allowed' 
                : 'border-white/50 hover:border-white/80 hover:bg-white/10 cursor-pointer'
            ]"
            @click="!(props.isEditing || props.disableAdd) && emit('addTask')"
            data-testid="add-task-button"
          >
          
           <i class="uil uil-plus-circle text-lg text-echoes-primary"></i> 
          
            <span>Add Task</span>
          </button>
        </div>
      </div>
    </div>
  </template>
  
  
  
